import { useEffect, useState, useRef } from "react"

import { X } from "lucide-react"

import { Button } from "@ui/button"
import { Input } from "@ui/input"
import { Label } from "@ui/label"
import { DateFilter } from "@/components/filters/DateFilter"
import { formatPropertyName } from "@/lib/properties"
import { FilterProperty } from "@/models/facets"

interface ProjectFilterPropertySectionProps {
    properties: FilterProperty[]
    onFilterChange: (filters: Record<string, any>) => void
    onClear: number
}
export function ProjectFilterPropertySection({ properties, onFilterChange, onClear }: Readonly<ProjectFilterPropertySectionProps>) {
    const debounceTimerRef = useRef<NodeJS.Timeout | null>(null)
    const [filters, setFilters] = useState<Record<string, any>>({})

    const handleStringFilterChange = (field: string, value: string) => {
        // Update local state with the actual value (including spaces)
        const newFilters = { ...filters, [field]: value }
        setFilters(newFilters)

        // If value is empty or only whitespace, clear the filter from parent
        if (value.trim() === "") {
            handleClearFilter(field)
        } else {
            debouncedFilterChange(newFilters)
        }
    }

    const handleDateFilterChange = (field: string, date: Date | undefined) => {
        const newFilters = { ...filters, [field]: date }
        setFilters(newFilters)
        debouncedFilterChange(newFilters)
    }

    const handleClearFilter = (field: string) => {
        const newFilters = { ...filters }
        delete newFilters[field]
        setFilters(newFilters)

        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current)
        }

        // Immediately notify parent about the cleared filter
        const cleanedFilters = Object.fromEntries(
            Object.entries(newFilters).filter(([key, value]) =>
                value !== null && value !== undefined && value !== ""
            )
        )

        const matches = Object.fromEntries(
            Object.entries(cleanedFilters).filter(([key, value]) => {
                const prop = properties.find(p => p.field === key)
                return prop && prop.match
            })
        )

        const filterProps = Object.fromEntries(
            Object.entries(cleanedFilters).filter(([key, value]) => {
                const prop = properties.find(p => p.field === key)
                return prop && !prop.match
            })
        )

        // Explicitly set the cleared field to undefined in the appropriate object
        const prop = properties.find(p => p.field === field)
        if (prop?.match) {
            matches[field] = undefined
        } else {
            filterProps[field] = undefined
        }

        onFilterChange({ properties: filterProps, matches })
    }

    const debouncedFilterChange = (newFilters: Record<string, any>) => {
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current)
        }

        debounceTimerRef.current = setTimeout(() => {
            const cleanedFilters = Object.fromEntries(
                Object.entries(newFilters).filter(([key, value]) =>
                    value !== null && value !== undefined && value !== ""
                )
            )

            const matches = Object.fromEntries(
                Object.entries(cleanedFilters).filter(([key]) => {
                    const prop = properties.find(p => p.field === key)
                    return prop && prop.match
                })
            )

            const filters = Object.fromEntries(
                Object.entries(cleanedFilters).filter(([key]) => {
                    const prop = properties.find(p => p.field === key)
                    return prop && !prop.match
                })
            )

            onFilterChange({ properties: filters, matches })
        }, 300)
    }

    useEffect(() => {
        setFilters({})

        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current)
        }
    }, [onClear])

    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current)
            }
        }
    }, [])

    return (
        <div className="flex w-full min-w-0 flex-col border-b gap-2 p-4">
            <Label className="shrink-0 items-center rounded-md text-base font-medium text-sidebar-foreground">
                Filter By
            </Label>
            <div className="flex flex-col gap-2">
                {properties.map((property, index) => {
                    const currentValue = filters[property.field]
                    const hasValue = currentValue !== undefined && currentValue !== null && currentValue !== ""

                    return (
                        <div key={index}>
                            <Label className="text-xs">{formatPropertyName(property.name)}</Label>
                            {
                                property.type === 'string' ? (
                                    <div className="relative">
                                        <Input
                                            placeholder={`Filter by ${formatPropertyName(property.name)}...`}
                                            value={currentValue || ""}
                                            onChange={(e) => handleStringFilterChange(property.field, e.target.value)}
                                            className="pr-8 bg-white"
                                        />
                                        {
                                            (hasValue) && (
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="absolute right-1 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0 hover:bg-gray-100"
                                                    onClick={() => handleClearFilter(property.field)}
                                                    type="button"
                                                >
                                                    <X className="h-3 w-3" />
                                                </Button>
                                            )
                                        }
                                    </div>
                                ) : (
                                    <DateFilter
                                        property={property}
                                        value={currentValue}
                                        onDateChange={(date) => handleDateFilterChange(property.field, date)}
                                        onClear={() => handleClearFilter(property.field)}
                                    />
                                )
                            }
                        </div>
                    )
                })}
            </div>
        </div>
    )
}